import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Crown, Play, Lock, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import RecRow from "@/components/RecRow";
import RewardedAd from "@/components/RewardedAd";

export default function Reader() {
  const { slug, number } = useParams();
  const num = parseInt(number);
  const { user, theme, toggleTheme } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [gate, setGate] = useState(null); // 'ad' | 'premium'
  const [adState, setAdState] = useState("idle"); // idle | loading | playing | completing | error | limit
  const [adsCfg, setAdsCfg] = useState({ countdown: 10, fallback_message: "" });
  const [ad, setAd] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    setGate(null); setData(null);
    try {
      const { data } = await api.get(`/reader/${slug}/${num}`);
      setData(data);
      await api.post("/reader/progress", { book_id: data.book.id, current_chapter: num });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "ad_required") setGate("ad");
      else if (d === "premium_required") setGate("premium");
      else if (e?.response?.status === 401) nav("/login");
      else toast(apiError(e));
    }
  }, [slug, num]); // eslint-disable-line

  useEffect(() => { if (user === undefined) return; if (!user) { nav("/login"); return; } load(); }, [load, user]); // eslint-disable-line
  useEffect(() => { api.get("/ads-config").then((r) => setAdsCfg(r.data)); }, []);
  useEffect(() => { if (gate === "ad" && user) api.get("/me/ad-usage").then((r) => setUsage(r.data)); }, [gate, user]);
  useEffect(() => { window.scrollTo(0, 0); }, [num]);

  const startAd = async () => {
    if (usage && usage.remaining <= 0) { setAdState("limit"); return; }
    setAdState("loading");
    try {
      const { data } = await api.get("/reader/ad", { params: { slug, number: num } });
      if (!data.ad) { setAdState("error"); return; }
      setAd(data);
      setAdState("playing");
    } catch { setAdState("error"); }
  };

  const unlock = async (watched, duration) => {
    const { data } = await api.post("/reader/unlock", { slug, number: num, view_id: ad.view_id, watch_seconds: watched, video_duration: duration });
    setUsage(data.usage);
    setData({ book: data.book, chapter: data.chapter, total: data.total });
    setGate(null); setAdState("idle"); setAd(null);
  };
  const adFailed = (e) => {
    setAd(null);
    if (e?.response?.status === 429) setAdState("limit");
    else { setAdState("error"); if (e) toast.error(apiError(e)); }
  };

  if (gate) {
    const remaining = usage ? usage.remaining : null;
    const warn = remaining != null && remaining <= 5 && remaining > 0;
    return (
      <div className="min-h-screen grid place-items-center px-3 py-6 bg-background">
        <div className="relative max-w-lg w-full border border-border rounded-3xl p-4 pt-12 sm:p-8 sm:pt-12 surface-2 fade-in text-center">
          <button onClick={() => nav(`/books/${slug}`)} data-testid="gate-close" aria-label="Close" className="absolute top-3 right-3 p-2 hover:text-primary"><X className="h-5 w-5" /></button>

          {gate === "premium" ? (
            <>
              <Crown className="h-12 w-12 text-amber-400 mx-auto" />
              <h2 className="text-2xl font-display font-bold mt-4">Premium Chapter</h2>
              <p className="text-muted-foreground mt-2">This story is exclusive to Premium members.</p>
              <Link to="/premium" className="inline-block mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">Go Premium</Link>
            </>
          ) : adState === "playing" && ad ? (
            <RewardedAd key={ad.view_id} session={ad} onUnlock={unlock} onFail={adFailed} />
          ) : adState === "limit" ? (
            <div data-testid="ad-limit-reached">
              <Lock className="h-12 w-12 text-amber-400 mx-auto" />
              <h2 className="text-2xl font-display font-bold mt-4">Daily ad limit reached</h2>
              <p className="text-muted-foreground mt-2">You've used all {usage?.limit || 30} free ad unlocks for today. Upgrade to Premium for uninterrupted reading, or come back tomorrow.</p>
              <div className="flex flex-col gap-3 mt-6">
                <Link to="/premium" data-testid="limit-go-premium" className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">Go Premium</Link>
                <button onClick={() => nav(`/books/${slug}`)} className="px-6 py-3 rounded-full border border-border font-semibold">Come Back Tomorrow</button>
              </div>
            </div>
          ) : (
            <>
              <Lock className="h-12 w-12 text-primary mx-auto" />
              <h2 className="text-2xl font-display font-bold mt-4">Chapter Locked</h2>
              <p className="text-muted-foreground mt-2">Watch a short ad to unlock this chapter.</p>
              {usage && (
                <div className="mt-5 rounded-xl border border-border p-3" data-testid="ad-usage-badge">
                  <p className="text-sm font-semibold">Daily Ad Unlocks: {usage.used} / {usage.limit}</p>
                  <p className={`text-xs mt-0.5 ${warn ? "text-amber-400" : "text-muted-foreground"}`}>
                    {remaining === 1 ? "You have 1 ad unlock remaining today." : `${remaining} unlocks remaining today.`}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-3 mt-6">
                <button onClick={startAd} disabled={adState === "loading"} data-testid="watch-ad-btn" className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 inline-flex items-center justify-center gap-2">
                  <Play className="h-4 w-4 fill-current" /> {adState === "loading" ? "Loading ad…" : "Watch Ad & Unlock"}
                </button>
                {adState === "error" && <p className="text-sm text-destructive" data-testid="ad-error">{adsCfg.fallback_message || "Ad unavailable. Please try again."} <button onClick={startAd} className="underline ml-1">Retry</button></p>}
                <Link to="/premium" className="px-6 py-3 rounded-full border border-amber-400/50 text-amber-400 font-semibold hover:bg-amber-400/10 transition-colors">Go Premium — read ad-free</Link>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  const { book, chapter, total } = data;

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to={`/books/${slug}`} className="p-2 -ml-2 hover:text-primary"><X className="h-5 w-5" /></Link>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{book.title}</p>
            <p className="text-xs text-muted-foreground">Chapter {chapter.number} of {total}</p>
          </div>
          <button onClick={toggleTheme} className="p-2 hover:text-primary">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
        </div>
      </div>

      <article className="max-w-2xl mx-auto px-5 py-12">
        <p className="track-label text-xs text-primary text-center">Chapter {chapter.number}</p>
        <h1 className="text-3xl md:text-4xl font-display font-bold text-center mt-2 mb-10">{chapter.title}</h1>
        <div className="prose-reader text-lg leading-[1.9] text-foreground/90 space-y-5" data-testid="chapter-content">
          {chapter.content.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </article>

      <div className="max-w-2xl mx-auto px-5 pb-20 flex items-center justify-between">
        <button disabled={num <= 1} onClick={() => nav(`/read/${slug}/${num - 1}`)} data-testid="prev-chapter"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full border border-border disabled:opacity-40 hover:border-primary transition-colors">
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        {num < total ? (
          <button onClick={() => nav(`/read/${slug}/${num + 1}`)} data-testid="next-chapter"
            className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90">
            Next Chapter <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <Link to={`/books/${slug}`} className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">✓ Finished</Link>
        )}
      </div>
      {num >= total && (
        <div className="max-w-5xl mx-auto px-5 pb-24" data-testid="read-next-section">
          <RecRow endpoint={`/books/${slug}/similar?limit=8`} title="Read Next" testid="read-next" compact />
        </div>
      )}
    </div>
  );
}
